import { Path } from "@ostack.tech/kform";
import { useCallback } from "react";

import { InvalidPathError } from "../utils/errors.ts";
import {
  formatTemporalAsString,
  parseStringAsTemporal,
  type Temporal,
} from "../utils/temporalUtils.ts";
import { useLatestValues } from "../utils/useLatestValues.ts";
import type { Controller } from "./useController.ts";
import type { FormatterControllerState } from "./useFormatter.ts";
import {
  type InputOptions,
  type InputOwnController,
  useInput,
} from "./useInput.ts";

/** Options available to the {@link useTemporalInput} hook. */
export type TemporalInputOptions<
  T extends Temporal | null = Temporal | null,
  TFormatted = string,
  TInput = HTMLInputElement,
> = Omit<InputOptions<T, TFormatted, TInput>, "format" | "parse"> &
  TemporalInputOwnOptions<T, TFormatted, TInput>;

/** Own options available to the {@link useTemporalInput} hook. */
export interface TemporalInputOwnOptions<
  T extends Temporal | null = Temporal | null,
  TFormatted = string,
  TInput = HTMLInputElement,
> {
  formatFromString?: (
    stringValue: string,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => TFormatted;
  parseToString?: (
    formattedValue: TFormatted,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => string;
}

/**
 * Controller used to read and control the form value associated with the form
 * input, exposing properties that should be set on the input itself.
 */
export type TemporalInputController<
  T extends Temporal | null = Temporal | null,
  TFormatted = string,
  TInput = HTMLInputElement,
> = Controller<T> & TemporalInputOwnController<TFormatted, TInput>;

/** Own temporal input controller. */
export type TemporalInputOwnController<
  TFormatted = string,
  TInput = HTMLInputElement,
> = InputOwnController<TFormatted, TInput>;

/** Allowed temporal types. */
const ALLOWED_TEMPORAL_TYPES = [
  "Instant",
  "LocalDate",
  "LocalDateTime",
  "Date",
];

/**
 * Hook controlling the integration between a temporal value (`Instant`,
 * `LocalDate`, `LocalDateTime`, or `Date`) of the form manager and an input of
 * the form.
 *
 * @param path Path of the temporal form value to access, relative to the
 *   current path.
 * @param options Available options.
 * @returns A controller used to read and control the temporal form value
 *   associated with the form input, exposing properties that should be set on
 *   the input itself.
 * @throws {InvalidPathError} When the schema at {@link path} is not temporal
 *   (not `Instant`, `LocalDate`, `LocalDateTime`, or `Date`).
 */
export function useTemporalInput<
  T extends Temporal | null = Temporal | null,
  TFormatted = string,
  TInput = HTMLInputElement,
>(
  path?: Path | string,
  options?: undefined,
): TemporalInputController<T, TFormatted, TInput>;
export function useTemporalInput<
  T extends Temporal | null = Temporal | null,
  TFormatted = string,
  TInput = HTMLInputElement,
>(
  path: Path | string | undefined,
  options: TemporalInputOptions<T, TFormatted, TInput>,
): TemporalInputController<T, TFormatted, TInput>;
export function useTemporalInput<
  T extends Temporal | null = Temporal | null,
  TFormatted = string,
  TInput = HTMLInputElement,
>(
  path: Path | string = Path.CURRENT,
  {
    formatFromString,
    parseToString,
    ...options
  }: TemporalInputOptions<T, TFormatted, TInput> = {},
): TemporalInputController<T, TFormatted, TInput> {
  const latestOptions = useLatestValues({ parseToString });

  // Input options
  const format = useCallback(
    (
      value: T | undefined,
      state: FormatterControllerState<T, TFormatted>,
      input: TInput | null,
    ): TFormatted => {
      const temporalString = formatTemporalAsString(value, state.schema);
      return formatFromString
        ? formatFromString(temporalString, state, input)
        : (temporalString as TFormatted);
    },
    [formatFromString],
  );
  const parse = useCallback(
    (
      formattedValue: TFormatted,
      state: FormatterControllerState<T, TFormatted>,
      input: TInput | null,
    ): T => {
      return parseStringAsTemporal(
        latestOptions.parseToString
          ? latestOptions.parseToString(formattedValue, state, input)
          : String(formattedValue ?? ""),
        state.schema,
      );
    },
    [latestOptions],
  );

  const inputController = useInput(path, { format, parse, ...options });

  // Validate schema type
  const { typeInfo } = inputController.useSchema();
  if (!ALLOWED_TEMPORAL_TYPES.includes(typeInfo.name)) {
    throw new InvalidPathError(
      inputController.getState().path,
      "Unsupported schema: Expected schema representing (possibly nullable) " +
        "temporal values, but got schema representing values of type " +
        `'${typeInfo.toString()}'.`,
    );
  }

  return inputController;
}
